import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Box,
  Typography,
  Stack,
  Divider,
} from "@mui/material";
import toast from "react-hot-toast";
import { formatNumber } from "../../../utils/formatNumber";

const EPSILON = 0.005;

const normalizeNumber = (value) => Number(value ?? 0) || 0;

const POExtraPriceDialog = ({ open, onClose, item, onSave }) => {
  const remainderDetail = useMemo(
    () => item?.details?.find((d) => d.priceSource !== "po") ?? null,
    [item],
  );

  const poDetail = useMemo(
    () => item?.details?.find((d) => d.priceSource === "po") ?? null,
    [item],
  );

  const basePrice = useMemo(() => {
    return normalizeNumber(
      remainderDetail?.basePrice ??
        poDetail?.basePrice ??
        item?.originalItem?.product?.price ??
        item?.originalItem?.pricePerUnit ??
        0,
    );
  }, [item, poDetail, remainderDetail]);

  const groupPrice = useMemo(() => {
    return normalizeNumber(
      remainderDetail?.groupPrice ??
        remainderDetail?.pricePerUnit ??
        item?.originalItem?.pricePerUnit ??
        basePrice,
    );
  }, [basePrice, item, remainderDetail]);

  const currentPrice = useMemo(() => {
    return normalizeNumber(
      remainderDetail?.pricePerUnit ??
        item?.originalItem?.pricePerUnit ??
        groupPrice,
    );
  }, [groupPrice, item, remainderDetail]);

  const poWeight = useMemo(
    () =>
      item?.details
        ?.filter((d) => d.priceSource === "po")
        .reduce((sum, d) => sum + normalizeNumber(d.usedWeight), 0) ?? 0,
    [item],
  );

  const overWeight = useMemo(
    () =>
      item?.details
        ?.filter((d) => d.priceSource !== "po")
        .reduce((sum, d) => sum + normalizeNumber(d.usedWeight), 0) ?? 0,
    [item],
  );

  const [mode, setMode] = useState("group");
  const [customPrice, setCustomPrice] = useState("");

  useEffect(() => {
    if (!open) return;

    const savedMode = item?.originalItem?.poExtraPriceMode ?? null;
    const savedCustom = item?.originalItem?.poExtraCustomPrice;

    if (savedMode) {
      setMode(savedMode);
      setCustomPrice(
        savedMode === "custom"
          ? String(normalizeNumber(savedCustom ?? currentPrice))
          : String(normalizeNumber(savedCustom ?? currentPrice)),
      );
      return;
    }

    if (Math.abs(currentPrice - basePrice) < EPSILON) {
      setMode("base");
      setCustomPrice(String(normalizeNumber(currentPrice)));
      return;
    }

    if (Math.abs(currentPrice - groupPrice) < EPSILON) {
      setMode("group");
      setCustomPrice(String(normalizeNumber(currentPrice)));
      return;
    }

    setMode("custom");
    setCustomPrice(String(normalizeNumber(currentPrice)));
  }, [basePrice, currentPrice, groupPrice, item, open]);

  const handleConfirm = () => {
    const custom = normalizeNumber(customPrice);
    let pricePerUnit = groupPrice;

    if (mode === "custom" && custom <= 0) {
      toast.error("กรุณากรอกราคากำหนดเอง");
      return;
    }

    if (mode === "base") {
      pricePerUnit = basePrice;
    } else if (mode === "group") {
      pricePerUnit = groupPrice;
    } else if (mode === "custom") {
      pricePerUnit = custom;
    }

    onSave?.({
      pricePerUnit,
      poExtraPriceMode: mode,
      poExtraCustomPrice: mode === "custom" ? custom : null,
    });
  };

  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>เลือกราคาส่วนเกิน PO</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Box>
            <Typography sx={{ fontWeight: 800, color: "text.secondary" }}>
              สินค้า
            </Typography>
            <Typography sx={{ fontWeight: 900, fontSize: 18 }}>
              {item?.productName || item?.originalItem?.productName || "-"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.5,
              p: 2,
              borderRadius: 2,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                น้ำหนัก PO
              </Typography>
              <Typography sx={{ fontWeight: 900 }}>
                {formatNumber(poWeight)} กก.
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                น้ำหนักส่วนเกิน
              </Typography>
              <Typography sx={{ fontWeight: 900 }}>
                {formatNumber(overWeight)} กก.
              </Typography>
            </Box>
          </Box>

          <Divider />

          <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
            <FormControlLabel
              value="base"
              control={<Radio />}
              label={
                <Box className="flex items-center justify-between gap-4 w-full">
                  <span className="font-bold">ราคาพื้นฐาน</span>
                  <span className="font-black text-slate-700">
                    {formatNumber(basePrice)} บ./กก.
                  </span>
                </Box>
              }
              sx={{ alignItems: "flex-start", mx: 0, py: 0.5 }}
            />

            <FormControlLabel
              value="group"
              control={<Radio />}
              label={
                <Box className="flex items-center justify-between gap-4 w-full">
                  <span className="font-bold">ราคากลุ่ม</span>
                  <span className="font-black text-slate-700">
                    {formatNumber(groupPrice)} บ./กก.
                  </span>
                </Box>
              }
              sx={{ alignItems: "flex-start", mx: 0, py: 0.5 }}
            />

            <FormControlLabel
              value="custom"
              control={<Radio />}
              label={
                <Box className="flex items-center justify-between gap-4 w-full">
                  <span className="font-bold">ราคากำหนดเอง</span>
                  <span className="font-black text-slate-700">
                    {formatNumber(normalizeNumber(customPrice || currentPrice))} บ./กก.
                  </span>
                </Box>
              }
              sx={{ alignItems: "flex-start", mx: 0, py: 0.5 }}
            />
          </RadioGroup>

          {mode === "custom" && (
            <TextField
              label="กรอกราคากำหนดเอง"
              value={customPrice}
              onChange={(e) => setCustomPrice(e.target.value)}
              type="number"
              fullWidth
              inputProps={{ step: "0.01", min: "0" }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>ยกเลิก</Button>
        <Button variant="contained" onClick={handleConfirm}>
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default POExtraPriceDialog;
